// Phase 2.10 (hh-1hz) — EditItemScreen.
//
// Manual edit form for a single item: rename, change quantity, relocate,
// delete. Ported from designproject/files/screens-forms.jsx
// (`EditItemScreen`). Backend:
//   - PATCH /api/v1/items/{id}        — name / quantity
//   - POST  /api/v1/items/{id}/move   — relocation to a room
//   - DELETE /api/v1/items/{id}       — behind a simple confirm dialog
//
// There is no GET /api/v1/items/{id}; the item is located inside the
// property tree (GET /api/v1/properties/tree), which also feeds the
// PlacePicker. The lookup mirrors useItem.locate — it matches items both
// under a room AND directly on a property (room-less items, hh-o11);
// missing the latter made "Править" fail to find them (hh-rgw, case-34).
// If the id is absent from the tree the screen shows an error.

import type { JSX } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { T, Btn, resolveIconName } from '../../tokens';
import { ScreenHeader } from '../../shell';
import type { RouterApi } from '../../router';
import { items, humanizeError } from '../../api';
import type { PropertyTreeNode } from '../../api/endpoints/properties.js';
import { usePropertyTree } from './usePropertyTree';
import { PlacePicker } from './PlacePicker';
import { IconPicker } from './IconPicker';
import { FormLabel, FormInput, FormFooter, FormError } from './formPrimitives';

export interface EditItemScreenProps {
  readonly router: RouterApi;
  readonly itemId: string;
}

/** Where an item sits. `roomId` is null for items attached straight to a property. */
interface ItemPlace {
  readonly propertyId: string;
  readonly roomId: string | null;
}

interface LocatedItem {
  readonly name: string;
  readonly quantity: number;
  /** Текущее значение icon: имя иконки (ручной выбор) или null (авто). */
  readonly icon: string | null;
  readonly place: ItemPlace;
}

/** Walks the tree to find the item — under a room or directly on a property. */
function locateItem(
  tree: readonly PropertyTreeNode[],
  itemId: string,
): LocatedItem | null {
  for (const property of tree) {
    for (const direct of property.items) {
      if (direct.id === itemId) {
        return {
          name: direct.name,
          quantity: direct.quantity,
          icon: direct.icon ?? null,
          place: { propertyId: property.id, roomId: null },
        };
      }
    }
    for (const room of property.rooms) {
      for (const it of room.items) {
        if (it.id === itemId) {
          return {
            name: it.name,
            quantity: it.quantity,
            icon: it.icon ?? null,
            place: { propertyId: property.id, roomId: room.id },
          };
        }
      }
    }
  }
  return null;
}

function describeError(err: unknown): string {
  return humanizeError(err, 'save');
}

function parseQuantity(raw: string): number {
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

export function EditItemScreen({ router, itemId }: EditItemScreenProps): JSX.Element {
  const tree = usePropertyTree();
  const [name, setName] = useState<string>('');
  const [quantity, setQuantity] = useState<string>('1');
  const [place, setPlace] = useState<ItemPlace | null>(null);
  // null = авто-подбор иконки; строка = ручной выбор.
  const [icon, setIcon] = useState<string | null>(null);
  const [original, setOriginal] = useState<LocatedItem | null>(null);
  const [hydrated, setHydrated] = useState<boolean>(false);
  const [pending, setPending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Hydrate the form once from the tree.
  useEffect(() => {
    if (tree.status !== 'ready' || hydrated) return;
    const found = locateItem(tree.properties, itemId);
    if (found !== null) {
      setName(found.name);
      setQuantity(String(found.quantity));
      setPlace(found.place);
      setIcon(found.icon);
      setOriginal(found);
    }
    setHydrated(true);
  }, [tree.status, tree.properties, itemId, hydrated]);

  const notFound = hydrated && original === null;

  const autoIcon = useMemo(() => resolveIconName('item', name), [name]);

  const canSave = useMemo<boolean>(
    () => original !== null && name.trim().length > 0 && place !== null && !pending,
    [original, name, place, pending],
  );

  const back = (): void => router.back();

  const save = (): void => {
    if (original === null || place === null || name.trim().length === 0) return;
    setPending(true);
    setError(null);

    const trimmed = name.trim();
    const qty = parseQuantity(quantity);
    const iconChanged = icon !== original.icon;
    const fieldsChanged =
      trimmed !== original.name || qty !== original.quantity || iconChanged;
    // A move happens only when the PlacePicker yields a room different from
    // the original. The picker can only select rooms, so `place.roomId` is a
    // string here; a room-less item left untouched keeps roomId === null.
    const targetRoomId = place.roomId;
    const moved = targetRoomId !== null && targetRoomId !== original.place.roomId;

    const steps: Promise<unknown>[] = [];
    if (fieldsChanged) {
      // icon в PATCH шлём только при изменении: строка → ручная установка,
      // null → сброс на авто-подбор (см. контракт hh-345).
      steps.push(
        items.updateItem(itemId, {
          name: trimmed,
          quantity: qty,
          ...(iconChanged ? { icon } : {}),
        }),
      );
    }
    if (moved) {
      steps.push(
        items.moveItem(itemId, {
          toRoomId: targetRoomId,
          toPropertyId: place.propertyId,
        }),
      );
    }
    // Run sequentially so a move applies on top of the rename cleanly.
    steps
      .reduce<Promise<unknown>>((chain, step) => chain.then(() => step), Promise.resolve())
      .then(() => {
        router.back();
      })
      .catch((err: unknown) => {
        setError(describeError(err));
        setPending(false);
      });
  };

  const remove = (): void => {
    if (pending) return;
    const ok = window.confirm(`Удалить «${original?.name ?? 'вещь'}»? Это действие можно откатить из истории.`);
    if (!ok) return;
    setPending(true);
    setError(null);
    items
      .deleteItem(itemId)
      .then(() => {
        router.go('tree');
      })
      .catch((err: unknown) => {
        setError(describeError(err));
        setPending(false);
      });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg }}>
      <ScreenHeader title="Править вещь" subtitle="Связи и история сохранятся" onBack={back} />
      <div style={{ flex: 1, overflow: 'auto', padding: '4px 16px 16px' }}>
        {tree.status === 'loading' && (
          <div style={{ fontSize: 12.5, color: T.ink3, padding: '6px 2px' }}>Загружаю…</div>
        )}
        {tree.status === 'error' && tree.error !== null && (
          <FormError message={tree.error} />
        )}
        {notFound && <FormError message="Вещь не найдена — возможно, уже удалена." />}

        {tree.status === 'ready' && original !== null && (
          <>
            <FormLabel>Имя</FormLabel>
            <FormInput value={name} onChange={setName} disabled={pending} />

            <FormLabel style={{ marginTop: 18 }}>Количество</FormLabel>
            <FormInput
              value={quantity}
              onChange={setQuantity}
              inputMode="numeric"
              disabled={pending}
            />

            <div style={{ marginTop: 18 }}>
              <IconPicker
                value={icon}
                onChange={setIcon}
                autoIcon={autoIcon}
                disabled={pending}
              />
            </div>

            <FormLabel style={{ marginTop: 18 }}>Место</FormLabel>
            <PlacePicker
              tree={tree.properties}
              value={
                place !== null && place.roomId !== null
                  ? { propertyId: place.propertyId, roomId: place.roomId }
                  : null
              }
              onChange={setPlace}
            />
            {place !== null && place.roomId === null && (
              <div style={{ fontSize: 12, color: T.ink3, padding: '6px 2px 0' }}>
                Сейчас вещь привязана к собственности без комнаты. Выбери комнату, чтобы
                переместить, или оставь как есть.
              </div>
            )}

            <Btn
              kind="danger"
              icon="trash"
              onClick={remove}
              disabled={pending}
              style={{ marginTop: 18, width: '100%' }}
            >
              Удалить вещь
            </Btn>

            {error !== null && <FormError message={error} />}
          </>
        )}
      </div>
      <FormFooter
        onCancel={back}
        onConfirm={save}
        confirmLabel="Сохранить"
        confirmIcon="check"
        disabled={!canSave}
        pending={pending}
      />
    </div>
  );
}
